import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { groupService } from "@/services/groupService";

/**
 * Componente para ver usuarios de un grupo
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Todos los componentes con size="small"
 * - dense={true} en listas
 * - Máximo 500 líneas
 */

interface Group {
  id: string;
  nombre: string;
  descripcion: string;
  activo: boolean;
  es_sistema: boolean;
}

interface User {
  id: string;
  email: string;
  nombre: string;
  apellido: string;
  nombreCompleto?: string;
  telefono?: string;
  activo: boolean;
  ultimo_login?: string;
  fecha_creacion: string;
}

interface GroupUsersProps {
  open: boolean;
  onClose: () => void;
  group: Group | null;
}

export function GroupUsers({ open, onClose, group }: GroupUsersProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar usuarios cuando se abre el modal
  useEffect(() => {
    if (open && group) {
      loadUsers();
    }
  }, [open, group]);

  const loadUsers = async () => {
    if (!group) return;

    setLoading(true);
    setError(null);

    try {
      const users = await groupService.getGroupUsers(group.id);
      setUsers(users);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error cargando usuarios del grupo",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getInitials = (nombre: string, apellido: string) => {
    return `${nombre.charAt(0).toUpperCase()}${apellido.charAt(0).toUpperCase()}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1}>
          <PeopleIcon color="primary" />
          <Stack>
            <Typography variant="h6">Usuarios del Grupo</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Grupo: {group?.nombre}
              </Typography>
              {group?.es_sistema && (
                <Chip
                  label="Sistema"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Stack spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Cargando usuarios...
              </Typography>
            </Stack>
          ) : (
            <>
              {/* Información del grupo */}
              {group?.descripcion && (
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Descripción del grupo:</strong> {group.descripcion}
                  </Typography>
                </Alert>
              )}

              {/* Resumen */}
              <Stack direction="row" spacing={2}>
                <Typography variant="subtitle1">
                  Total de usuarios: {users.length}
                </Typography>
                {users.length > 0 && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      Activos: {users.filter((u) => u.activo).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactivos: {users.filter((u) => !u.activo).length}
                    </Typography>
                  </>
                )}
              </Stack>

              {/* Lista de usuarios */}
              {users.length === 0 ? (
                <Alert severity="warning">
                  <Stack spacing={1}>
                    <PersonIcon color="disabled" />
                    <Typography variant="body2">
                      No hay usuarios asignados a este grupo
                    </Typography>
                  </Stack>
                </Alert>
              ) : (
                <List dense>
                  {users.map((user, index) => (
                    <React.Fragment key={user.id}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar color={user.activo ? "primary" : "default"}>
                            {getInitials(user.nombre, user.apellido)}
                          </Avatar>
                        </ListItemAvatar>
                        <Stack sx={{ flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography variant="body2">
                              {user.nombreCompleto ||
                                `${user.nombre} ${user.apellido}`}
                            </Typography>
                            <Chip
                              size="small"
                              label={user.activo ? "Activo" : "Inactivo"}
                              color={user.activo ? "success" : "default"}
                              variant={user.activo ? "filled" : "outlined"}
                            />
                          </Stack>
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <EmailIcon color="action" sx={{ fontSize: 14 }} />
                              <Typography variant="caption">
                                {user.email}
                              </Typography>
                            </Stack>
                            {user.telefono && (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <PhoneIcon
                                  color="action"
                                  sx={{ fontSize: 14 }}
                                />
                                <Typography variant="caption">
                                  {user.telefono}
                                </Typography>
                              </Stack>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Último login:{" "}
                              {user.ultimo_login
                                ? formatDate(user.ultimo_login)
                                : "Nunca"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Miembro desde: {formatDate(user.fecha_creacion)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </ListItem>
                      {index < users.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}

              {/* Información adicional */}
              <Alert severity="info">
                <Typography variant="body2">
                  Los usuarios en este grupo heredan automáticamente todos los
                  permisos asignados al grupo. Para gestionar usuarios
                  individuales, ve a la sección de Usuarios.
                </Typography>
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small" startIcon={<CloseIcon />}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
